import React from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, IconButton, Skeleton } from '@mui/material';
import { Close as CloseIcon, Inventory2Outlined as MachineIcon, SearchOff as NoResultsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { colors, spacing } from '../../themes/typography';
import CommandCenterTopBar from './components/CommandCenterTopBar';
import MachineStrip from './components/MachineStrip';
import EnhancedPlanogram from './components/EnhancedPlanogram';
import FloatingActionBar from './components/FloatingActionBar';
import AISuggestionPopover from './components/AISuggestionPopover';
import CreateExperimentForm from '../../components/shared/CreateExperimentForm';
import BatchExperimentDialog from './components/BatchExperimentDialog';
import SlotDetailDrawer from './components/SlotDetailDrawer';
import useSlotSelection from './hooks/useSlotSelection';
import { fetchMachinesData, fetchMachineFilterOptions, lookupMachineByNumber } from '../../data/machinesApi';
import { fetchCandidateSuggestion } from '../ExperimentManagement/services/experimentManagementApi';
import { useAuth } from '../../context/AuthContext';
import { POSAnalysisTab } from './POSAnalysisTab';

export const OverviewTab = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentUser } = useAuth();

  // Initialize state with filters from sessionStorage
  const [state, setState] = React.useState(() => {
    const savedFilters = sessionStorage.getItem('analysis-page-filters');
    const filters = savedFilters ? JSON.parse(savedFilters) : { group: '', location: '' };

    return {
      filters,
      filterOptions: { groups: [] },
      availableLocations: [],
      availableMachines: [],
      machinesData: [],
      selectedMachineId: null
    };
  });

  // Initialize experimentStates
  const [experimentStates, setExperimentStates] = React.useState({});

  // View mode + period + category overlay
  const [viewMode, setViewMode] = React.useState('products');
  const [categoryOverlay, setCategoryOverlay] = React.useState(false);
  const [period, setPeriod] = React.useState(() => {
    const saved = sessionStorage.getItem('machines-period');
    return saved ? parseInt(saved) : 14;
  });

  // Destructure state for easier access
  const { filters, filterOptions, availableLocations, machinesData, selectedMachineId } = state;

  // Derive selectedMachine from machinesData + selectedMachineId
  const selectedMachine = React.useMemo(() => {
    if (!selectedMachineId || !machinesData.length) return null;
    return machinesData.find(m => m.machineId === selectedMachineId) || null;
  }, [selectedMachineId, machinesData]);

  // Loading states (not cached)
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  // Slot selection
  const { selectedSlots, toggleSlot, clearSelection, getSelectedSlotData, isActMode } = useSlotSelection();

  // Experiment form state (single)
  const [formOpen, setFormOpen] = React.useState(false);
  const [formInitialValues, setFormInitialValues] = React.useState(null);

  // Batch experiment state
  const [batchFormOpen, setBatchFormOpen] = React.useState(false);
  const [batchFormSlots, setBatchFormSlots] = React.useState([]);
  const [batchExperimentType, setBatchExperimentType] = React.useState('promo_precio_unitario');
  const [batchAiSuggestions, setBatchAiSuggestions] = React.useState(null);

  // Slot detail drawer state — store slot key, derive live data from enriched machine
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerSlotKey, setDrawerSlotKey] = React.useState(null);

  // Ref to hold a pending machine ID to auto-select after machines load
  const pendingMachineIdRef = React.useRef(null);

  // AI suggestion state
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiSuggestion, setAiSuggestion] = React.useState(null);
  const [aiAnchorEl, setAiAnchorEl] = React.useState(null);

  // Clear slot selection when machine changes
  React.useEffect(() => {
    clearSelection();
    setDrawerOpen(false);
    setDrawerSlotKey(null);
  }, [selectedMachineId, clearSelection]);

  // Stable callback for experiment state changes
  const handleExperimentStateChange = React.useCallback((newStatesOrUpdater) => {
    setExperimentStates(prev => {
      const newState = typeof newStatesOrUpdater === 'function' ? newStatesOrUpdater(prev) : newStatesOrUpdater;
      return newState;
    });
  }, []);

  // Period change handler
  const handlePeriodChange = React.useCallback((newPeriod) => {
    setPeriod(newPeriod);
    sessionStorage.setItem('machines-period', String(newPeriod));
  }, []);

  // Define loadMachineData first so it can be used in refreshDataAfterMutation
  // isRefresh=true avoids the skeleton flash (keeps stale data visible while loading)
  const loadMachineData = React.useCallback(async (isRefresh = false) => {
    if (!filters.group) return;

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const filteredMachines = await fetchMachinesData({ ...filters, period }, currentUser.customer_id);

      // Clear selected machine if it's no longer in the filtered results
      const newSelectedMachineId = selectedMachineId && !filteredMachines.find(m => m.machineId === selectedMachineId)
        ? null
        : selectedMachineId;

      setState(prev => ({
        ...prev,
        machinesData: filteredMachines,
        selectedMachineId: newSelectedMachineId
      }));

      // Clear optimistic experiment states — backend data now has full experiment info
      setExperimentStates({});

    } catch (error) {
      setState(prev => ({
        ...prev,
        machinesData: [],
        selectedMachineId: null
      }));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, period, currentUser.customer_id, selectedMachineId]);

  // Function to refresh data after any mutation (experiment accept/reject, etc.)
  // Does a silent background refresh without nulling the selected machine
  const refreshDataAfterMutation = React.useCallback(async () => {
    if (filters.group) {
      await loadMachineData(true); // isRefresh=true avoids skeleton flash
    }
  }, [filters.group, loadMachineData]);

  // Optimistic update: instantly show experiment badge on a slot after creation
  const addOptimisticExperiment = React.useCallback((machineId, row, col, experimentData) => {
    const machineKey = `${machineId}-${row}-${col}`;
    setExperimentStates(prev => ({
      ...prev,
      [machineKey]: {
        id: experimentData?.experiment?.id || Date.now(),
        status: experimentData?.experiment?.status || 'pending',
        experimentType: experimentData?.experiment?.experimentType || 'promo_precio_unitario',
      }
    }));
  }, []);

  // Listen for experiment state changes that require data refresh
  React.useEffect(() => {
    const handleExperimentMutation = () => {
      refreshDataAfterMutation();
    };

    window.addEventListener('experimentMutation', handleExperimentMutation);

    return () => {
      window.removeEventListener('experimentMutation', handleExperimentMutation);
    };
  }, [refreshDataAfterMutation]);

  // Save filters to sessionStorage whenever they change
  React.useEffect(() => {
    sessionStorage.setItem('analysis-page-filters', JSON.stringify(filters));
  }, [filters]);

  // Load filter options on component mount
  React.useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await fetchMachineFilterOptions(currentUser.customer_id);
        setState(prev => ({
          ...prev,
          filterOptions: options
        }));

        // Check for filters passed from navigation state (from insights/diagnostics page)
        const passedFilters = location.state?.filters;
        const passedMachineStoreId = location.state?.machineStoreId;

        if (passedMachineStoreId && currentUser?.customer_id) {
          // Lookup machine by store_id and set filters + pending selection
          try {
            const machineInfo = await lookupMachineByNumber(currentUser.customer_id, passedMachineStoreId);
            if (machineInfo) {
              pendingMachineIdRef.current = String(machineInfo.id);
              setState(prev => ({
                ...prev,
                selectedMachineId: null,
                filters: {
                  ...prev.filters,
                  group: machineInfo.group || '',
                  location: machineInfo.location || ''
                }
              }));
            }
          } catch (err) {
            console.error('[Machines] lookupMachine from nav state failed:', err);
          }
        } else if (passedFilters) {
          setState(prev => ({
            ...prev,
            filters: {
              ...prev.filters,
              group: passedFilters.group || '',
              location: passedFilters.location || ''
            }
          }));
        }
      } catch (error) {
      }
    };

    if (currentUser?.customer_id) {
      loadFilterOptions();
    }
  }, [location.state, currentUser]);

  // Clear sidebar on mount and unmount
  React.useEffect(() => {
    window.currentPageSidebar = null;
    window.dispatchEvent(new CustomEvent('sidebarUpdate'));
    return () => {
      window.currentPageSidebar = null;
      window.dispatchEvent(new CustomEvent('sidebarUpdate'));
    };
  }, []);

  // Filter handlers
  const handleFilterChange = (filterType, value) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [filterType]: value
      }
    }));
  };

  // When group changes, update available locations
  React.useEffect(() => {
    if (!filterOptions.groups || filterOptions.groups.length === 0) {
      return;
    }

    const selectedGroup = filterOptions.groups.find(g => g.id === filters.group);
    const allLocations = [];

    selectedGroup?.clusters?.forEach(cluster => {
      if (cluster.locations) {
        allLocations.push(...cluster.locations);
      }
    });

    const currentLocationIsValid = allLocations.some(loc => loc.id === filters.location);

    setState(prev => ({
      ...prev,
      availableLocations: allLocations,
      filters: {
        ...prev.filters,
        location: currentLocationIsValid ? prev.filters.location : ''
      }
    }));
  }, [filters.group, filterOptions]);

  // Auto-search when filters change
  const prevGroupRef = React.useRef(filters.group);
  React.useEffect(() => {
    if (filters.group) {
      // Treat group change as a fresh load (show skeleton), same-group filter tweaks as refresh
      const groupChanged = prevGroupRef.current !== filters.group;
      prevGroupRef.current = filters.group;
      const isRefresh = !groupChanged && machinesData.length > 0;

      // Immediately show loading skeleton on group change (don't wait for debounce)
      if (groupChanged) {
        setLoading(true);
        setState(prev => ({ ...prev, machinesData: [], selectedMachineId: null }));
      }

      const timeoutId = setTimeout(() => {
        loadMachineData(isRefresh);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      prevGroupRef.current = filters.group;
      setState(prev => ({
        ...prev,
        machinesData: [],
        selectedMachineId: null
      }));
    }
  }, [filters.group, filters.location, period]);

  // Auto-select the first machine when machines load (or pending machine from search/navigation)
  React.useEffect(() => {
    if (!loading && machinesData.length > 0 && !selectedMachineId) {
      if (pendingMachineIdRef.current) {
        const pending = String(pendingMachineIdRef.current);
        pendingMachineIdRef.current = null;
        const found = machinesData.find(m => String(m.machineId) === pending);
        setState(prev => ({ ...prev, selectedMachineId: found ? found.machineId : machinesData[0].machineId }));
      } else {
        setState(prev => ({ ...prev, selectedMachineId: machinesData[0].machineId }));
      }
    }
  }, [loading, machinesData, selectedMachineId]);

  // Handle machine selection from strip or top bar
  const handleSelectMachine = React.useCallback((machineId) => {
    setState(prev => ({ ...prev, selectedMachineId: machineId }));
  }, []);

  // Handle machine number search from CommandCenterTopBar
  const handleMachineSearch = React.useCallback(async (storeId, option) => {
    if (!currentUser?.customer_id) return;
    try {
      // If option has group/location (from dropdown), use directly — no API call needed
      const machineInfo = option?.group
        ? { id: option.id, group: option.group, location: option.location }
        : await lookupMachineByNumber(currentUser.customer_id, storeId);

      if (machineInfo) {
        const machineId = String(machineInfo.id);
        const filtersChanged = machineInfo.group !== filters.group || machineInfo.location !== filters.location;

        if (filtersChanged) {
          pendingMachineIdRef.current = machineId;
          setState(prev => ({
            ...prev,
            selectedMachineId: null,
            filters: {
              ...prev.filters,
              group: machineInfo.group || '',
              location: machineInfo.location || ''
            }
          }));
        } else {
          setState(prev => ({ ...prev, selectedMachineId: machineId }));
        }
      }
    } catch (err) {
      console.error('[Machines] Machine lookup failed:', err);
    }
  }, [currentUser, filters.group, filters.location]);

  // Helper function to enrich machine data with experiment states
  const enrichMachineData = React.useCallback((machineData) => {
    if (!machineData) return machineData;

    return {
      ...machineData,
      slots: machineData.slots.map(slot => {
        const productKey = `${slot.row}-${slot.column}`;
        const machineKey = `${machineData.machineId}-${productKey}`;
        const experimentState = experimentStates[machineKey];

        if (experimentState) {
          return {
            ...slot,
            problem: slot.problem,
            experiment: experimentState
          };
        }

        return slot;
      })
    };
  }, [experimentStates]);

  // Enriched selected machine
  const enrichedSelectedMachine = React.useMemo(() => {
    return selectedMachine ? enrichMachineData(selectedMachine) : null;
  }, [selectedMachine, enrichMachineData]);

  // Derive live drawer slot data from enriched machine
  const drawerSlotData = React.useMemo(() => {
    if (!drawerSlotKey || !enrichedSelectedMachine?.slots) return null;
    return enrichedSelectedMachine.slots.find(
      s => s.row === drawerSlotKey.row && s.column === drawerSlotKey.column
    ) || null;
  }, [drawerSlotKey, enrichedSelectedMachine]);

  // Experiment creation from slot selection
  const handleCreateExperiment = React.useCallback((type = 'promo_precio_unitario') => {
    if (!enrichedSelectedMachine || selectedSlots.size === 0) return;

    const selected = getSelectedSlotData(enrichedSelectedMachine.slots);
    if (selected.length === 0) return;

    if (selected.length > 1) {
      // Multi-select: open batch dialog
      setBatchFormSlots(selected);
      setBatchExperimentType(type);
      setBatchFormOpen(true);
    } else {
      // Single slot: open regular form
      const firstSlot = selected[0];
      setFormInitialValues({
        dStoreId: enrichedSelectedMachine.machineId,
        dProductId: firstSlot.d_product_id,
        machineAlias: enrichedSelectedMachine.location,
        productName: firstSlot.name,
        experimentType: type,
        laneType: firstSlot.laneType,
        selectionId: firstSlot.selection_id
      });
      setFormOpen(true);
    }
  }, [enrichedSelectedMachine, selectedSlots, getSelectedSlotData]);

  // Inspect: click on slot opens drawer (does NOT affect selection)
  const handleSlotInspect = React.useCallback((slot) => {
    if (slot && slot.name) {
      setDrawerSlotKey({ row: slot.row, column: slot.column });
      setDrawerOpen(true);
    }
  }, []);

  // Toggle: checkbox click toggles slot selection (does NOT open drawer)
  const handleSlotToggle = React.useCallback((slot) => {
    toggleSlot(slot);
  }, [toggleSlot]);

  // AI suggestion handler — supports single and multi-slot
  const handleAISuggest = React.useCallback(async (event) => {
    if (!enrichedSelectedMachine || selectedSlots.size === 0) return;

    const selected = getSelectedSlotData(enrichedSelectedMachine.slots);
    if (selected.length === 0) return;

    setAiLoading(true);
    setAiAnchorEl(event.currentTarget);

    try {
      if (selected.length === 1) {
        // Single slot: return object as before
        const result = await fetchCandidateSuggestion(
          currentUser.customer_id,
          enrichedSelectedMachine.machineId,
          selected[0].d_product_id,
          'reemplazo_producto'
        );
        setAiSuggestion(result);
      } else {
        // Multi-slot: fetch in parallel, return array of { slot, suggestion }
        const results = await Promise.allSettled(
          selected.map(slot =>
            fetchCandidateSuggestion(
              currentUser.customer_id,
              enrichedSelectedMachine.machineId,
              slot.d_product_id,
              'reemplazo_producto'
            ).then(suggestion => ({ slot, suggestion }))
          )
        );
        const suggestions = results
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value);
        // Include slots where AI returned empty candidates (so popover shows all slots)
        const coveredSlots = new Set(suggestions.map(s => `${s.slot.row}-${s.slot.column}`));
        const allSlotSuggestions = [
          ...suggestions,
          ...selected
            .filter(slot => !coveredSlots.has(`${slot.row}-${slot.column}`))
            .map(slot => ({ slot, suggestion: { candidates: [] } }))
        ];
        setAiSuggestion(allSlotSuggestions);
      }
    } catch {
      setAiSuggestion({ suggestion: t('machines_aiSuggestionError') });
    } finally {
      setAiLoading(false);
    }
  }, [enrichedSelectedMachine, selectedSlots, getSelectedSlotData, currentUser.customer_id]);

  // AI suggestion from the slot drawer (alerted slot, no experiment yet) — acts on that slot only
  const handleDrawerAISuggest = React.useCallback(async (slot, anchorEl) => {
    if (!enrichedSelectedMachine || !slot) return;

    // Apply path creates the experiment from the selection → make this slot the selection
    if (!(selectedSlots.size === 1 && selectedSlots.has(`${slot.row}-${slot.column}`))) {
      clearSelection();
      toggleSlot(slot);
    }

    setAiLoading(true);
    setAiAnchorEl(anchorEl);
    try {
      const result = await fetchCandidateSuggestion(
        currentUser.customer_id,
        enrichedSelectedMachine.machineId,
        slot.d_product_id,
        'reemplazo_producto'
      );
      setAiSuggestion(result);
    } catch {
      setAiSuggestion({ suggestion: t('machines_aiSuggestionError') });
    } finally {
      setAiLoading(false);
    }
  }, [enrichedSelectedMachine, selectedSlots, clearSelection, toggleSlot, currentUser.customer_id]);

  // Apply AI suggestion → open create form with replacement type
  // Passes AI suggestions to batch dialog for per-slot candidate pre-population
  const handleApplyAISuggestion = React.useCallback((suggestion) => {
    const isMulti = Array.isArray(suggestion);
    setAiSuggestion(null);
    setAiAnchorEl(null);

    if (isMulti && suggestion.length > 1) {
      // Multi-slot: open batch dialog with AI suggestions attached
      const selected = getSelectedSlotData(enrichedSelectedMachine.slots);
      setBatchFormSlots(selected);
      setBatchExperimentType('reemplazo_producto');
      setBatchAiSuggestions(suggestion);
      setBatchFormOpen(true);
    } else {
      handleCreateExperiment('reemplazo_producto');
    }
  }, [handleCreateExperiment, getSelectedSlotData, enrichedSelectedMachine]);

  // Quick-discount: create experiment with 15% discount for 8 weeks, single slot only
  const handleQuickDiscount = React.useCallback(async () => {
    if (!enrichedSelectedMachine || selectedSlots.size !== 1) return;

    const selected = getSelectedSlotData(enrichedSelectedMachine.slots);
    if (selected.length !== 1) return;

    const slot = selected[0];
    const dateFrom = new Date();
    dateFrom.setMonth(dateFrom.getMonth() + 1, 1); // 1st of next month
    dateFrom.setHours(0, 0, 0, 0);
    const dateTo = new Date(dateFrom);
    dateTo.setDate(dateTo.getDate() + 56); // 8 weeks from start

    setFormInitialValues({
      dStoreId: enrichedSelectedMachine.machineId,
      dProductId: slot.d_product_id,
      machineAlias: enrichedSelectedMachine.location,
      productName: slot.name,
      experimentType: 'promo_precio_unitario',
      discount: 15,
      dateFrom: dateFrom.toISOString().split('T')[0],
      dateTo: dateTo.toISOString().split('T')[0]
    });
    setFormOpen(true);
  }, [enrichedSelectedMachine, selectedSlots, getSelectedSlotData]);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!machinesData.length) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'Escape') {
        if (drawerOpen) {
          setDrawerOpen(false);
          return;
        }
        clearSelection();
        return;
      }

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const currentIndex = machinesData.findIndex(m => m.machineId === selectedMachineId);
        let nextIndex;
        if (e.key === 'ArrowRight') {
          nextIndex = currentIndex < machinesData.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : machinesData.length - 1;
        }
        setState(prev => ({ ...prev, selectedMachineId: machinesData[nextIndex].machineId }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [machinesData, selectedMachineId, drawerOpen]);

  return (
    <Box sx={{
      backgroundColor: colors.background.page,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Sticky Top Bar */}
      <CommandCenterTopBar
        filters={filters}
        filterOptions={filterOptions}
        availableLocations={availableLocations}
        onFilterChange={handleFilterChange}
        machinesData={machinesData}
        selectedMachineId={selectedMachineId}
        onSelectMachine={handleSelectMachine}
        selectedMachine={enrichedSelectedMachine}
        loading={loading}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        period={period}
        onPeriodChange={handlePeriodChange}
        categoryOverlay={categoryOverlay}
        onCategoryOverlayChange={setCategoryOverlay}
        onMachineSearch={handleMachineSearch}
      />

      {/* Main Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
        {/* Loading skeleton */}
        {loading && (
          <Box sx={{ py: 2 }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)' },
              gap: '6px',
              maxWidth: 900,
              mx: 'auto'
            }}>
              {Array.from({ length: 30 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: '6px' }} />
              ))}
            </Box>
          </Box>
        )}

        {/* No machines found */}
        {!loading && machinesData.length === 0 && filters.group && (
          <Box sx={{
            textAlign: 'center',
            py: spacing.xxl,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5
          }}>
            <NoResultsIcon sx={{ fontSize: 48, color: colors.text.muted, opacity: 0.5 }} />
            <Typography sx={{ fontSize: '15px', fontWeight: 500, color: colors.text.secondary }}>
              {t('machines_noMachinesFound')}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: colors.text.muted }}>
              {t('machines_tryAdjustingFilters')}
            </Typography>
          </Box>
        )}

        {/* No group selected */}
        {!loading && machinesData.length === 0 && !filters.group && (
          <Box sx={{
            textAlign: 'center',
            py: spacing.xxl,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5
          }}>
            <MachineIcon sx={{ fontSize: 48, color: colors.text.muted, opacity: 0.5 }} />
            <Typography sx={{ fontSize: '15px', fontWeight: 500, color: colors.text.secondary }}>
              {t('machines_selectGroupToStart')}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: colors.text.muted }}>
              {t('machines_chooseGroupMessage')}
            </Typography>
          </Box>
        )}

        {/* Planogram (hero) */}
        {!loading && enrichedSelectedMachine && (
          <Box
            key={enrichedSelectedMachine.machineId}
            sx={{
              animation: 'fadeIn 200ms ease-out',
              '@keyframes fadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 }
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none'
              },
              pb: isActMode ? 8 : 0, // Bottom padding when floating action bar is visible
              opacity: refreshing ? 0.5 : 1,
              transition: 'opacity 200ms ease',
              pointerEvents: refreshing ? 'none' : 'auto'
            }}
          >
            <EnhancedPlanogram
              machineData={enrichedSelectedMachine}
              selectedSlots={selectedSlots}
              onSlotInspect={handleSlotInspect}
              onSlotToggleSelect={handleSlotToggle}
              isActMode={isActMode}
              onMachineClick={(machine) => {
                setState(prev => ({ ...prev, selectedMachineId: enrichedSelectedMachine.machineId }));
              }}
              heatmapMode={viewMode}
              onHeatmapModeChange={setViewMode}
              compact={true}
              period={period}
              categoryOverlay={categoryOverlay}
            />
          </Box>
        )}
      </Box>

      {/* Floating Action Bar */}
      <FloatingActionBar
        selectedCount={selectedSlots.size}
        selectedSlots={enrichedSelectedMachine ? getSelectedSlotData(enrichedSelectedMachine.slots) : []}
        onCreateDiscount={() => handleCreateExperiment('promo_precio_unitario')}
        onCreateReplacement={() => handleCreateExperiment('reemplazo_producto')}
        onAISuggest={handleAISuggest}
        aiLoading={aiLoading}
        onClear={clearSelection}
        onToggleSelect={handleSlotToggle}
        onQuickDiscount={handleQuickDiscount}
      />

      {/* AI Suggestion Popover */}
      <AISuggestionPopover
        anchorEl={aiAnchorEl}
        open={Boolean(aiAnchorEl && aiSuggestion)}
        onClose={() => { setAiSuggestion(null); setAiAnchorEl(null); }}
        suggestion={aiSuggestion}
        onApply={handleApplyAISuggestion}
      />

      {/* Single Create Experiment Dialog */}
      <Dialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setFormInitialValues(null); }}
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: '12px', overflow: 'visible' }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 0,
          fontSize: '16px',
          fontWeight: 600
        }}>
          {t('createExperiment')}
          <IconButton
            size="small"
            onClick={() => { setFormOpen(false); setFormInitialValues(null); }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <CreateExperimentForm
            open={formOpen}
            column={{ id: 'accepted' }}
            clientId={currentUser.customer_id}
            currentFilters={filters}
            filterOptions={filterOptions}
            initialValues={formInitialValues}
            lockMachine={true}
            lockProduct={selectedSlots.size === 1 || (selectedSlots.size === 0 && !!formInitialValues?.dProductId)}
            presetSelection={formInitialValues ? {
              dStoreId: formInitialValues.dStoreId,
              machineLabel: formInitialValues.machineAlias,
              dProductId: formInitialValues.dProductId,
              productLabel: formInitialValues.productName
            } : undefined}
            onClose={() => { setFormOpen(false); setFormInitialValues(null); }}
            onSuccess={(responseData) => {
              // Optimistic update: show experiment badge instantly
              if (responseData?.experiment && formInitialValues && enrichedSelectedMachine) {
                const slot = enrichedSelectedMachine.slots.find(s =>
                  Number(s.d_product_id) === Number(formInitialValues.dProductId)
                );
                if (slot) {
                  addOptimisticExperiment(enrichedSelectedMachine.machineId, slot.row, slot.column, responseData);
                }
              }
              // Silent background refresh
              refreshDataAfterMutation();
              setFormOpen(false);
              setFormInitialValues(null);
              clearSelection();
            }}
            onError={() => {}}
            t={t}
          />
        </DialogContent>
      </Dialog>

      {/* Batch Experiment Dialog */}
      <BatchExperimentDialog
        open={batchFormOpen}
        onClose={() => { setBatchFormOpen(false); setBatchFormSlots([]); setBatchAiSuggestions(null); }}
        slots={batchFormSlots}
        experimentType={batchExperimentType}
        machineData={enrichedSelectedMachine}
        clientId={currentUser.customer_id}
        filters={filters}
        filterOptions={filterOptions}
        aiSuggestions={batchAiSuggestions}
        onSuccess={(createdSlots) => {
          // Optimistic update: show experiment badges instantly for all created slots
          if (Array.isArray(createdSlots) && enrichedSelectedMachine) {
            createdSlots.forEach(slot => {
              if (slot._experimentResponse) {
                addOptimisticExperiment(
                  enrichedSelectedMachine.machineId,
                  slot.row,
                  slot.column,
                  slot._experimentResponse
                );
              }
            });
          }
          // Silent background refresh
          refreshDataAfterMutation();
          setBatchFormOpen(false);
          setBatchFormSlots([]);
          setBatchAiSuggestions(null);
          clearSelection();
        }}
      />

      {/* Slot Detail Drawer */}
      <SlotDetailDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerSlotKey(null); }}
        slot={drawerSlotData}
        machineData={enrichedSelectedMachine}
        clientId={currentUser.customer_id}
        onAISuggest={handleDrawerAISuggest}
        aiSuggestLoading={aiLoading}
        onCreateExperiment={(type) => {
          setDrawerOpen(false);
          // If no slots selected but drawer has a slot, directly open the form
          if (selectedSlots.size === 0 && drawerSlotData && enrichedSelectedMachine) {
            setFormInitialValues({
              dStoreId: enrichedSelectedMachine.machineId,
              dProductId: drawerSlotData.d_product_id,
              machineAlias: enrichedSelectedMachine.location,
              productName: drawerSlotData.name,
              experimentType: 'reemplazo_producto',
              laneType: drawerSlotData.laneType,
              selectionId: drawerSlotData.selection_id
            });
            setFormOpen(true);
          } else {
            handleCreateExperiment(type);
          }
        }}
        selectedSlots={selectedSlots}
        onToggleSelect={handleSlotToggle}
        period={period}
      />
    </Box>
  );
};


const Analysis = () => null;

export { POSAnalysisTab };
export default Analysis;
